import React from "react";
import { Eye } from "lucide-react";
import { ScopingSchema, ScopingSection } from "./scoping-schema";
import { isChoiceType } from "./scoping-question-ui";

type Props = {
  schema: ScopingSchema | null;
  section: ScopingSection | null;
  sectionIndex: number;
  totalSections: number;
  onSectionChange: (idx: number) => void;
  templateName?: string;
};

const PreviewField: React.FC<{ q: any }> = ({ q }) => {
  if (q.type === "long_text") {
    return (
      <div className="h-16 rounded-lg border border-slate-200 bg-slate-50/80" />
    );
  }
  if (isChoiceType(q.type)) {
    return (
      <div className="space-y-1.5">
        {(q.options || []).slice(0, 4).map((opt: any, i: number) => {
          const label = typeof opt === "string" ? opt : opt.label;
          return (
            <div
              key={i}
              className="flex items-center gap-2 text-[11px] text-slate-600"
            >
              <span className="w-3.5 h-3.5 rounded-full border-2 border-slate-300 shrink-0" />
              {label}
            </div>
          );
        })}
        {(q.options?.length || 0) > 4 && (
          <span className="text-[10px] text-slate-400">+{(q.options?.length || 0) - 4} more</span>
        )}
      </div>
    );
  }
  if (q.type === "yes_no") {
    return (
      <div className="flex gap-2">
        <span className="px-3 py-1 rounded-md border border-slate-200 text-[11px] text-slate-500">Yes</span>
        <span className="px-3 py-1 rounded-md border border-slate-200 text-[11px] text-slate-500">No</span>
      </div>
    );
  }
  if (q.type === "target_builder") {
    return (
      <div className="rounded-lg border border-dashed border-ctb-blue/30 bg-ctb-blue-0/20 px-3 py-4 text-[11px] text-ctb-blue font-medium text-center">
        Target & methodology builder
      </div>
    );
  }
  return <div className="h-9 rounded-lg border border-slate-200 bg-slate-50/80" />;
};

export const ScopingFormPreview: React.FC<Props> = ({
  schema,
  section,
  sectionIndex,
  totalSections,
  onSectionChange,
  templateName
}) => {
  if (!schema?.sections?.length) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[280px] text-center p-6">
        <Eye size={28} className="text-slate-300 mb-2" strokeWidth={1.5} />
        <p className="text-[11px] text-slate-500">Add sections to see a client preview.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50 shrink-0">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Client preview</p>
        {templateName && (
          <p className="text-xs font-bold text-slate-800 truncate mt-0.5">{templateName}</p>
        )}
        <div className="flex items-center gap-2 mt-2">
          <button
            type="button"
            disabled={sectionIndex <= 0}
            onClick={() => onSectionChange(sectionIndex - 1)}
            className="text-[10px] font-medium text-ctb-blue disabled:text-slate-300"
          >
            Prev
          </button>
          <span className="text-[10px] text-slate-400 tabular-nums">
            {sectionIndex + 1} / {totalSections}
          </span>
          <button
            type="button"
            disabled={sectionIndex >= totalSections - 1}
            onClick={() => onSectionChange(sectionIndex + 1)}
            className="text-[10px] font-medium text-ctb-blue disabled:text-slate-300"
          >
            Next
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gradient-to-b from-white to-slate-50/80">
        {section ? (
          <>
            <div>
              <h3 className="text-sm font-bold text-slate-900">{section.title || "Untitled section"}</h3>
              {section.description && (
                <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">{section.description}</p>
              )}
            </div>
            <div className="space-y-3">
              {section.questions.map((q) => (
                <div
                  key={q.qid}
                  className="p-3 rounded-lg border border-slate-200/90 bg-white shadow-[0_1px_2px_rgba(8,21,73,0.04)] space-y-2"
                >
                  <label className="text-[11px] font-semibold text-slate-800 leading-snug block">
                    {q.label || "Untitled"}
                    {q.required && <span className="text-emerald-600 ml-0.5">*</span>}
                  </label>
                  <PreviewField q={q} />
                </div>
              ))}
              {section.questions.length === 0 && (
                <p className="text-[11px] text-slate-400 italic text-center py-6">No questions in this section.</p>
              )}
            </div>
          </>
        ) : (
          <p className="text-[11px] text-slate-400 text-center py-8">Select a section.</p>
        )}
      </div>
    </div>
  );
};
